// plants/model.js — turns the indoor plants' HA entities and their soil
// moisture statistics into what the Plants page and the home-screen line
// draw. Pure functions, so the rules can be tested without React.
//
// Built from the canvas Ben approved on 2026-10-22. What comes from where:
//   - moisture: each plant's soil sensor (ha-plant-monitor)
//   - three lines per plant, all OpenPlantbook-based and set in HA:
//       water at  `input_number.threshold_<id>`  (OPB minimum plus a buffer;
//                 the phone alerts use the same number)
//       very dry  `number.<id>_min_soil_moisture` (OPB minimum)
//       too wet   `number.<id>_max_soil_moisture` (OPB maximum)
//   - photo: the plant device's `entity_picture` (OPB stock photo for now)
//   - care notes: `sensor.plant_care_<id>`, refreshed from OPB daily
//     (ha-plant-monitor packages/plant_care_notes.yaml)
//   - last watered: worked out from jumps in the hourly readings, no button
//   - sensor heard: the gateways' per-sensor `signal` (ha-irrigation
//     soil_sensor_liveness). The moisture entity itself cannot say: Ecowitt
//     does not refresh its timestamp when the value is unchanged.
//
// Not shown to the family: the plant devices' conductivity "problem" (the
// Office Peace Lily reads it permanently against OPB's 350 floor).

export const PLANTS = [
  { id: 'corn_plant', name: 'Corn Plant', room: 'Living Room' },
  { id: 'pineapple_dracaena', name: 'Pineapple Dracaena', room: 'Living Room' },
  { id: 'peace_lily', name: 'Peace Lily', room: 'Dining Room' },
  { id: 'office_peace_lily', name: 'Office Peace Lily', room: 'Office' },
];

export const ids = (id) => ({
  moisture: `sensor.${id}_soil_moisture`,
  water: `input_number.threshold_${id}`,
  min: `number.${id}_min_soil_moisture`,
  max: `number.${id}_max_soil_moisture`,
  plant: `plant.${id}`,
  care: `sensor.plant_care_${id}`,
  links: [`sensor.${id}_link_office`, `sensor.${id}_link_pavilion`],
});

// A rise of this many points within an hour is a watering.
export const WATERING_JUMP = 8;
// Within this many points above the watering point reads "Water soon".
export const SOON_POINTS = 2;
// Slower than this (points a day) is "drying slowly": no estimate.
export const SLOW_RATE = 0.3;

const BLANK = new Set(['unknown', 'unavailable', 'none', '']);
function num(states, id) {
  const e = states?.[id];
  if (!e || e.state == null || BLANK.has(String(e.state).toLowerCase())) return null;
  const n = Number(e.state);
  return Number.isFinite(n) ? n : null;
}

// ── Now ────────────────────────────────────────────────────────────

export function readPlant(states, p) {
  const e = ids(p.id);
  const plant = states?.[e.plant]?.attributes || {};
  const care = states?.[e.care];
  const links = e.links.map((l) => num(states, l)).filter((v) => v != null);
  return {
    ...p,
    entity: e.moisture,
    moisture: num(states, e.moisture),
    changedAt: states?.[e.moisture]?.last_changed ? new Date(states[e.moisture].last_changed)
      : states?.[e.moisture]?.last_updated ? new Date(states[e.moisture].last_updated) : null,
    water: num(states, e.water),
    min: num(states, e.min),
    max: num(states, e.max),
    image: plant.entity_picture || null,
    species: care && !BLANK.has(String(care.state).toLowerCase()) ? care.state : null,
    care: care?.attributes ? {
      watering: care.attributes.watering || null,
      sunlight: care.attributes.sunlight || null,
      fertilization: care.attributes.fertilization || null,
      soil: care.attributes.soil || null,
      pruning: care.attributes.pruning || null,
    } : null,
    // null when the link sensors are missing: unknown, not dead.
    heard: links.length ? links.some((v) => v > 0) : null,
  };
}

// kind: very_dry, needs, soon, fine, too_wet, unknown. `alert` marks the
// two that take the rust "needs you" treatment.
export function status(p) {
  const { moisture: m, water, min, max } = p;
  if (m == null || water == null) return { kind: 'unknown', label: 'No reading', alert: false };
  if (min != null && m < min) return { kind: 'very_dry', label: 'Very dry, water now', alert: true };
  if (m < water) return { kind: 'needs', label: 'Needs water', alert: true };
  if (max != null && m > max) return { kind: 'too_wet', label: 'Too wet', alert: false };
  if (m < water + SOON_POINTS) return { kind: 'soon', label: 'Water soon', alert: false };
  return { kind: 'fine', label: 'Fine', alert: false };
}

export function readPlants(states) {
  const order = { very_dry: 0, needs: 1, soon: 2, too_wet: 3, unknown: 4, fine: 5 };
  return PLANTS.map((p) => readPlant(states, p))
    .map((p) => ({ ...p, status: status(p) }))
    .sort((a, b) => order[a.status.kind] - order[b.status.kind]);
}

// ── History ────────────────────────────────────────────────────────

// `rows`: one sensor's hourly statistics ({start, max, mean}), oldest
// first. A watering is an hour whose maximum is WATERING_JUMP or more above
// the previous hour's mean; one within six hours of the last is the same
// watering. Its peak is the highest reading in the six hours after.
export function waterings(rows) {
  const out = [];
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1], r = rows[i];
    if (typeof r.max !== 'number' || typeof prev.mean !== 'number') continue;
    if (r.max - prev.mean < WATERING_JUMP) continue;
    const at = new Date(r.start);
    const last = out[out.length - 1];
    if (last && at - last.at < 6 * 3600000) continue;
    const peak = Math.max(...rows.slice(i, i + 6).map((x) => x.max ?? -Infinity));
    out.push({ at, peak });
  }
  return out;
}

// Points lost per day since the soil settled after the last watering
// (24 h after it), over at least a day and a half. null when there is not
// enough settled history to say.
export function dryingRate(rows, lastWatering, now) {
  const from = Math.max(
    lastWatering ? lastWatering.at.getTime() + 24 * 3600000 : 0,
    now.getTime() - 7 * 86400000,
  );
  const span = rows.filter((r) => r.start >= from && typeof r.mean === 'number');
  if (span.length < 2) return null;
  const a = span[0], b = span[span.length - 1];
  const days = (b.start - a.start) / 86400000;
  if (days < 1.5) return null;
  return Math.max(0, (a.mean - b.mean) / days);
}

// Daily means for the 30-day line, keyed by local date; a day without
// readings is simply absent, so the line breaks there.
export function dailyMeans(rows) {
  const by = new Map();
  for (const r of rows) {
    if (typeof r.mean !== 'number') continue;
    const d = localDate(new Date(r.start));
    if (!by.has(d)) by.set(d, []);
    by.get(d).push(r.mean);
  }
  return [...by.entries()].map(([date, v]) => ({ date, mean: v.reduce((s, x) => s + x, 0) / v.length }));
}

// `live` is the plant now ({moisture, changedAt}). Hourly statistics lag by
// up to an hour, so a watering since the last hour shows only in the live
// reading: one a watering-sized jump above the last hour counts, dated when
// the reading changed.
export function history(rows, now, live = null) {
  const w = waterings(rows);
  const lastRow = rows[rows.length - 1];
  if (live?.moisture != null && lastRow && typeof lastRow.mean === 'number'
      && live.moisture - lastRow.mean >= WATERING_JUMP
      && (!w.length || w[w.length - 1].at.getTime() < lastRow.start)) {
    w.push({ at: live.changedAt || now, peak: live.moisture });
  }
  const last = w[w.length - 1] || null;
  return { waterings: w, last, rate: dryingRate(rows, last, now), days: dailyMeans(rows), rows };
}

// ── Words ──────────────────────────────────────────────────────────

export function localDate(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const dayDiff = (a, b) => Math.round((new Date(localDate(b)) - new Date(localDate(a))) / 86400000);

// "This morning", "Yesterday", "Friday 9 Oct, 13 days ago".
export function wateredWhen(at, now) {
  if (!at) return null;
  const n = dayDiff(at, now);
  if (n === 0) return at.getHours() < 12 ? 'This morning' : at.getHours() < 17 ? 'This afternoon' : 'This evening';
  if (n === 1) return 'Yesterday';
  const day = at.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' });
  return `${day}, ${n} days ago`;
}

// The one sentence under the facts. Says what is happening, and only
// estimates when the drying is steady enough to be worth it.
export function note(p, h, now) {
  const s = p.status, m = p.moisture;
  if (p.heard === false) return 'The sensor has not been heard lately, so this reading may be old.';
  if (m == null) return null;
  const since = h?.last ? dayDiff(h.last.at, now) : null;
  const rate = h?.rate;
  if (s.kind === 'too_wet') return 'Let it dry out before watering again.';
  if (since != null && since <= 2 && (s.kind === 'soon' || s.kind === 'needs')) {
    return `Watered ${wateredWhen(h.last.at, now).toLowerCase()} to ${Math.round(h.last.peak)}% and already back at its watering point.`;
  }
  if (s.kind === 'needs' || s.kind === 'very_dry') {
    if (rate != null && rate >= SLOW_RATE && p.min != null && m > p.min) {
      const d = Math.max(1, Math.round((m - p.min) / rate));
      return `Drying about ${rateWords(rate)} a day, so very dry (${p.min}%) in about ${d} day${d === 1 ? '' : 's'}.`;
    }
    return null;
  }
  if (since === 0) return `Watered ${wateredWhen(h.last.at, now).toLowerCase()} to ${Math.round(h.last.peak)}%.`;
  if (rate != null && rate < SLOW_RATE) return 'Drying slowly.';
  if (rate != null && p.water != null && m > p.water) {
    const d = Math.max(1, Math.round((m - p.water) / rate));
    return `At about ${rateWords(rate)} a day, it reaches its watering point in about ${d} day${d === 1 ? '' : 's'}.`;
  }
  return null;
}
function rateWords(r) {
  if (r < 0.75) return 'half a point';
  if (r < 1.5) return 'a point';
  return `${Math.round(r)} points`;
}

// The home-screen line: null when nothing needs water.
export function homeLine(plants) {
  const due = plants.filter((p) => p.status.alert);
  if (!due.length) return null;
  if (due.length === 1) return { text: `${due[0].name} needs water`, sub: due[0].room };
  return { text: `${due.length} plants need water`, sub: due.map((p) => p.name).join(', ') };
}
